import { AxiosError, AxiosResponse } from 'axios';
import qs from 'qs-stringify';

import { RESPONSE_CODES } from '../constants/responseCodes.constants';
import { api } from '../http';
import { store } from '../store';
import { DefaultCallback, DefaultRes } from '../types';
import { AuditLogExplorerTypes } from '../types/auditLogExplorer.types';

const baseUri = 'auditLogExplorer';

/**
 * Audit Log Explorer service. Talks to the FROZEN endpoints:
 *   POST /v1/auditLogExplorer/search   — filtered, paginated search
 *   GET  /v1/auditLogExplorer/:mongoId — full single record for the drawer
 *   POST /v1/auditLogExplorer/rawSql   — read-only ad-hoc SELECT
 * All are gated to POM-Master (403 USER_MUST_BE_POM_MASTER otherwise).
 */
export class AuditLogExplorerService {
    private static get dispatch() {
        return store.dispatch.auditLogExplorer;
    }

    // Run token for getDetail. The warehouse is slow (~30s timeout) so rapid row
    // clicks can resolve out of order; only the newest call is allowed to write
    // the store, otherwise an older response could render under a newer header.
    private static detailRun = 0;

    public static async search(
        body: AuditLogExplorerTypes.SearchRequest,
        callback?: DefaultCallback,
    ): Promise<AuditLogExplorerTypes.SearchResult | void> {
        const { dispatch } = AuditLogExplorerService;
        try {
            dispatch.toggleLoading('search', true);

            const {
                data: { result, isValid, message },
            } = await api.post<
                DefaultRes<AuditLogExplorerTypes.SearchResult>,
                AxiosResponse<DefaultRes<AuditLogExplorerTypes.SearchResult>>
            >(`/${baseUri}/search`, body);

            dispatch.toggleLoading('search', false);

            if (isValid) {
                dispatch.setResult(result);
                callback && callback(true, RESPONSE_CODES[message]);
                return result;
            }

            callback && callback(false, RESPONSE_CODES[message] || message);
        } catch (error: any) {
            dispatch.toggleLoading('search', false);
            const code = (error as AxiosError<DefaultRes>).response?.data?.message;
            callback && callback(false, RESPONSE_CODES[code!] || code || String(error));
        }
    }

    public static async getDetail(
        mongoId: string,
        callback?: DefaultCallback,
    ): Promise<AuditLogExplorerTypes.DetailRow | void> {
        const { dispatch } = AuditLogExplorerService;
        const run = ++AuditLogExplorerService.detailRun;
        try {
            dispatch.toggleLoading('detail', true);
            dispatch.setDetail(null);

            const {
                data: { result, isValid, message },
            } = await api.get<
                DefaultRes<AuditLogExplorerTypes.DetailRow>,
                AxiosResponse<DefaultRes<AuditLogExplorerTypes.DetailRow>>
            >(`/${baseUri}/${mongoId}`);

            // A newer click started while this was in flight — drop this response
            // so the latest selection owns the loading flag and the drawer body.
            if (run !== AuditLogExplorerService.detailRun) return;
            dispatch.toggleLoading('detail', false);

            if (isValid) {
                dispatch.setDetail(result);
                callback && callback(true, RESPONSE_CODES[message]);
                return result;
            }

            callback && callback(false, RESPONSE_CODES[message] || message);
        } catch (error: any) {
            if (run !== AuditLogExplorerService.detailRun) return;
            dispatch.toggleLoading('detail', false);
            const code = (error as AxiosError<DefaultRes>).response?.data?.message;
            callback && callback(false, RESPONSE_CODES[code!] || code || String(error));
        }
    }

    /**
     * Run a read-only ad-hoc SELECT against the warehouse replica.
     * Columns are dynamic (driven by the query), so the caller renders from
     * `result.fields`. Errors surface as guard/exec codes in `message` (e.g.
     * RAW_SQL_ONLY_SELECT_ALLOWED, RAW_SQL_TIMEOUT). Kept store-free — the
     * console result is transient, component-local state.
     */
    public static async runRawSql(
        sql: string,
    ): Promise<{ ok: boolean; result: AuditLogExplorerTypes.RawSqlResult | null; message?: string }> {
        try {
            const {
                data: { result, isValid, message },
            } = await api.post<
                DefaultRes<AuditLogExplorerTypes.RawSqlResult>,
                AxiosResponse<DefaultRes<AuditLogExplorerTypes.RawSqlResult>>
            >(`/${baseUri}/rawSql`, { sql } as AuditLogExplorerTypes.RawSqlRequest);

            return { ok: isValid, result: isValid ? result : null, message };
        } catch (error: any) {
            const code = (error as AxiosError<DefaultRes>).response?.data?.message;
            return { ok: false, result: null, message: code || String(error) };
        }
    }

    /**
     * Resolve an organization name → subscriptionId for the filter typeahead.
     * The search contract only accepts subscriptionId[], so we look names up via
     * the existing customer `getMany` endpoint and hand back {name, subscriptionId}.
     * Read-only — does not touch the customers-admin store.
     */
    public static async searchOrganizations(
        name: string,
    ): Promise<AuditLogExplorerTypes.OrganizationOption[]> {
        try {
            const qsfy = qs({ perPage: 10, page: 1, filter: { name } });
            const {
                data: { result, isValid },
            } = await api.get<DefaultRes<{ items: { name: string; subscriptionId: string }[] }>>(
                `/customer/getMany?${qsfy}`,
            );
            if (!isValid || !result?.items) return [];
            return result.items
                .filter((c) => !!c.subscriptionId)
                .map((c) => ({ name: c.name, subscriptionId: c.subscriptionId }));
        } catch {
            return [];
        }
    }
}
